/**
 * Admin Stats API Contract
 *
 * Wire format for:
 *   GET /api/v1/admin/stats/sales  (analytics-service AdminStatsController)
 *   GET /api/v1/admin/stats/daily  (analytics-service AdminStatsController)
 *
 * Shared between analytics-service (producer) and admin frontend (consumer).
 * Both must import from here rather than defining local interfaces.
 */

import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Sales stats endpoint
// ---------------------------------------------------------------------------

export const AdminStatsByTicketTypeSchema = z.object({
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  totalOrders: z.number().int(),
  totalRevenue: z.number(),
});

export type AdminStatsByTicketType = z.infer<typeof AdminStatsByTicketTypeSchema>;

export const AdminSalesStatsQuerySchema = z.object({
  startDate: z.string().regex(DATE_REGEX).optional(),
  endDate: z.string().regex(DATE_REGEX).optional(),
});

export type AdminSalesStatsQuery = z.infer<typeof AdminSalesStatsQuerySchema>;

export const AdminSalesStatsResponseSchema = z.object({
  totalOrders: z.number().int(),
  totalRevenue: z.number(),
  byTicketType: z.array(AdminStatsByTicketTypeSchema),
  // #56: order lifecycle counts + rates (optional so older deployments don't break)
  paidCount: z.number().int().optional(),
  fulfilledCount: z.number().int().optional(),
  refundedCount: z.number().int().optional(),
  cancelledCount: z.number().int().optional(),
  rateDenominator: z.number().int().optional(),
  fulfillmentRate: z.number().optional(),
  refundRate: z.number().optional(),
  refundedAmountIdr: z.number().optional(),
});

export type AdminSalesStatsResponse = z.infer<typeof AdminSalesStatsResponseSchema>;

// ---------------------------------------------------------------------------
// Daily stats endpoint
// ---------------------------------------------------------------------------

export const AdminDailyDataPointSchema = z.object({
  date: z.string().regex(DATE_REGEX),
  revenue: z.number(),
  orders: z.number().int(),
  tickets: z.number().int(),
});

export type AdminDailyDataPoint = z.infer<typeof AdminDailyDataPointSchema>;

export const AdminDailyStatsResponseSchema = z.object({
  last30Days: z.array(AdminDailyDataPointSchema),
});

export type AdminDailyStatsResponse = z.infer<typeof AdminDailyStatsResponseSchema>;
